import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCountry } from '../../context/CountryContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { 
  Baby, ShoppingBag, Shirt, Gamepad2, Car, ChevronRight, 
  ChevronLeft, CheckCircle, Star, Package, Heart,
  ArrowRight, Sparkles, Shield, Share2, Copy, Mail, 
  Save, Loader2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Age stages
const AGE_STAGES = [
  { id: 'newborn', label: 'Newborn (0-3 months)', icon: Baby, description: 'Essential items for the first weeks' },
  { id: 'infant', label: 'Infant (3-6 months)', icon: Baby, description: 'Growing baby essentials' },
  { id: 'crawler', label: 'Crawler (6-12 months)', icon: Gamepad2, description: 'Mobility and play items' },
  { id: 'toddler', label: 'Toddler (1-3 years)', icon: Gamepad2, description: 'Active toddler necessities' },
  { id: 'expecting', label: "I'm Expecting", icon: Heart, description: 'Preparing for baby arrival' },
];

// Bundle categories with recommended items per age
const BUNDLE_CATEGORIES = {
  newborn: {
    essentials: [
      { id: 'cot', name: 'Baby Cot/Moses Basket', price: 120, icon: '🛏️', recommended: true },
      { id: 'mattress', name: 'Cot Mattress', price: 45, icon: '🛏️', recommended: true },
      { id: 'bedding', name: 'Bedding Set', price: 35, icon: '🛏️', recommended: true },
      { id: 'monitor', name: 'Baby Monitor', price: 65, icon: '📡', recommended: true },
      { id: 'changing_mat', name: 'Changing Mat', price: 20, icon: '🔄', recommended: true },
    ],
    clothing: [
      { id: 'sleepsuits', name: 'Sleepsuits (5 pack)', price: 25, icon: '👕', recommended: true },
      { id: 'bodysuits', name: 'Bodysuits (7 pack)', price: 20, icon: '👕', recommended: true },
      { id: 'hats', name: 'Baby Hats (3 pack)', price: 10, icon: '🧢', recommended: true },
      { id: 'mittens', name: 'Scratch Mittens', price: 8, icon: '🧤', recommended: false },
      { id: 'blankets', name: 'Swaddle Blankets (2)', price: 25, icon: '🧣', recommended: true },
    ],
    feeding: [
      { id: 'bottles', name: 'Bottle Set', price: 30, icon: '🍼', recommended: true },
      { id: 'steriliser', name: 'Steriliser', price: 55, icon: '✨', recommended: true },
      { id: 'bibs', name: 'Bibs (10 pack)', price: 15, icon: '🍴', recommended: true },
      { id: 'nursing_pillow', name: 'Nursing Pillow', price: 35, icon: '💝', recommended: false },
    ],
    travel: [
      { id: 'car_seat', name: 'Infant Car Seat', price: 120, icon: '🚗', recommended: true, safety: true },
      { id: 'pram', name: 'Pram/Stroller', price: 250, icon: '🛒', recommended: true },
      { id: 'changing_bag', name: 'Changing Bag', price: 40, icon: '👜', recommended: true },
    ],
  },
  infant: {
    essentials: [
      { id: 'bouncer', name: 'Baby Bouncer', price: 65, icon: '🎵', recommended: true },
      { id: 'play_mat', name: 'Play Mat/Gym', price: 45, icon: '🎮', recommended: true },
      { id: 'high_chair', name: 'High Chair', price: 80, icon: '🪑', recommended: true },
    ],
    clothing: [
      { id: 'rompers', name: 'Rompers (5 pack)', price: 30, icon: '👕', recommended: true },
      { id: 'leggings', name: 'Leggings (5 pack)', price: 25, icon: '👖', recommended: true },
    ],
    toys: [
      { id: 'rattles', name: 'Rattle Set', price: 15, icon: '🎲', recommended: true },
      { id: 'soft_toys', name: 'Soft Toys', price: 20, icon: '🧸', recommended: false },
      { id: 'teethers', name: 'Teething Toys', price: 12, icon: '🦷', recommended: true },
    ],
  },
  crawler: {
    essentials: [
      { id: 'walker', name: 'Baby Walker', price: 45, icon: '🚶', recommended: true },
      { id: 'safety_gates', name: 'Safety Gates (2)', price: 50, icon: '🚧', recommended: true, safety: true },
      { id: 'corner_protectors', name: 'Corner Protectors', price: 15, icon: '🛡️', recommended: true, safety: true },
    ],
    toys: [
      { id: 'stacking_toys', name: 'Stacking Toys', price: 18, icon: '🎲', recommended: true },
      { id: 'shape_sorter', name: 'Shape Sorter', price: 22, icon: '🔷', recommended: true },
      { id: 'push_toys', name: 'Push Along Toys', price: 25, icon: '🚂', recommended: true },
      { id: 'board_books', name: 'Board Books Set', price: 20, icon: '📚', recommended: true },
    ],
  },
  toddler: {
    essentials: [
      { id: 'toddler_bed', name: 'Toddler Bed', price: 100, icon: '🛏️', recommended: true },
      { id: 'potty', name: 'Potty/Toilet Seat', price: 25, icon: '🚽', recommended: true },
      { id: 'step_stool', name: 'Step Stool', price: 18, icon: '🪜', recommended: true },
    ],
    toys: [
      { id: 'building_blocks', name: 'Building Blocks', price: 30, icon: '🧱', recommended: true },
      { id: 'ride_on', name: 'Ride-On Toy', price: 45, icon: '🚗', recommended: true },
      { id: 'art_supplies', name: 'Art Supplies Set', price: 25, icon: '🎨', recommended: true },
      { id: 'play_kitchen', name: 'Play Kitchen', price: 80, icon: '🍳', recommended: false },
    ],
    clothing: [
      { id: 'shoes', name: 'First Shoes', price: 35, icon: '👟', recommended: true },
      { id: 'raincoat', name: 'Raincoat', price: 25, icon: '🧥', recommended: false },
    ],
  },
  expecting: {
    maternity: [
      { id: 'maternity_clothes', name: 'Maternity Basics', price: 80, icon: '👗', recommended: true },
      { id: 'pregnancy_pillow', name: 'Pregnancy Pillow', price: 40, icon: '💤', recommended: true },
      { id: 'hospital_bag', name: 'Hospital Bag Essentials', price: 50, icon: '🏥', recommended: true },
    ],
    nursery_prep: [
      { id: 'cot_prep', name: 'Cot & Mattress', price: 165, icon: '🛏️', recommended: true },
      { id: 'nursery_storage', name: 'Nursery Storage', price: 60, icon: '📦', recommended: false },
    ],
  },
};

// Item Card Component
function BundleItemCard({ item, selected, onToggle, currency }) {
  const currencySymbol = currency === 'LKR' ? 'Rs.' : '£';
  const price = currency === 'LKR' ? item.price * 400 : item.price;
  
  return (
    <div 
      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
        selected 
          ? 'border-indigo-500 bg-indigo-50' 
          : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={onToggle}
      data-testid={`bundle-item-${item.id}`}
    >
      <div className="flex items-center gap-3">
        <Checkbox checked={selected} />
        <span className="text-2xl">{item.icon}</span>
        <div className="flex-1">
          <p className="font-medium text-sm">{item.name}</p>
          <div className="flex items-center gap-2">
            <span className="text-indigo-600 font-semibold">{currencySymbol}{price}</span>
            {item.recommended && (
              <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                Recommended
              </Badge>
            )}
            {item.safety && (
              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                <Shield className="w-3 h-3 mr-1" /> Safety
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Component
export default function NewParentStarterKit() {
  const navigate = useNavigate();
  const { shareToken } = useParams();
  const { country: visitorCountry } = useCountry();
  const [step, setStep] = useState(1);
  // Default to visitor country (SL → LK) rather than hard-coding UK.
  const [country, setCountry] = useState(() => {
    const v = (visitorCountry || 'UK').toUpperCase();
    return v === 'SL' ? 'LK' : v;
  });
  // Keep in sync if user switches country via the footer switcher.
  useEffect(() => {
    const v = (visitorCountry || 'UK').toUpperCase();
    setCountry(v === 'SL' ? 'LK' : v);
  }, [visitorCountry]);
  const [selectedAge, setSelectedAge] = useState('');
  const [selectedItems, setSelectedItems] = useState({});
  const [budget, setBudget] = useState('medium'); // low, medium, high
  
  // New state for saving and sharing
  const [saving, setSaving] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [savedBundleId, setSavedBundleId] = useState(null);
  const [savedShareToken, setSavedShareToken] = useState(null);
  const [loadingShared, setLoadingShared] = useState(false);
  const [ageItems, setAgeItems] = useState({});
  
  const currency = country === 'LK' ? 'LKR' : 'GBP';
  const currencySymbol = currency === 'LKR' ? 'Rs.' : '£';
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Load shared bundle if shareToken is present
  useEffect(() => {
    if (shareToken) {
      loadSharedBundle(shareToken);
    }
  }, [shareToken]);
  
  // Fetch recommendations from API when age changes
  useEffect(() => {
    if (selectedAge) {
      fetchRecommendations(selectedAge, country);
    }
  }, [selectedAge, country]);
  
  const fetchRecommendations = async (age, countryCode) => {
    try {
      const response = await fetch(`${API_URL}/api/baby-kids/starter-kit/recommendations/${age}?country=${countryCode}`);
      const data = await response.json();
      if (data.success) {
        setAgeItems(data.categories || {});
      }
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
      // Fall back to hardcoded data
      setAgeItems(BUNDLE_CATEGORIES[age] || {});
    }
  };
  
  const loadSharedBundle = async (token) => {
    setLoadingShared(true);
    try {
      const response = await fetch(`${API_URL}/api/baby-kids/starter-kit/${token}`);
      const data = await response.json();
      
      if (data.success && data.bundle) {
        const bundle = data.bundle;
        setSelectedAge(bundle.age_stage);
        setCountry(bundle.country);
        
        // Reconstruct selected items
        const itemsMap = {};
        bundle.items.forEach(item => {
          itemsMap[item.id] = true;
        });
        setSelectedItems(itemsMap);
        setStep(3); // Go to review step
        toast.success('Shared bundle loaded!');
      }
    } catch (error) {
      console.error('Failed to load shared bundle:', error);
      toast.error('Failed to load shared bundle');
    } finally {
      setLoadingShared(false);
    }
  };
  
  const saveBundle = async () => {
    if (!userEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setSaving(true);
    try {
      // Build items array
      const items = [];
      Object.entries(ageItems).forEach(([category, categoryItems]) => {
        categoryItems.forEach(item => {
          if (selectedItems[item.id]) {
            items.push({
              id: item.id,
              name: item.name,
              price: item.price,
              icon: item.icon,
              category: category,
            });
          }
        });
      });
      
      const discountPercent = itemCount >= 10 ? 15 : itemCount >= 5 ? 10 : itemCount >= 3 ? 5 : 0;
      
      const response = await fetch(`${API_URL}/api/baby-kids/starter-kit/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: userEmail,
          user_name: userName,
          country: country,
          age_stage: selectedAge,
          items: items,
          total_price: totalPrice,
          discount_percent: discountPercent,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSavedBundleId(data.bundle_id);
        setSavedShareToken(data.share_token);
        setShowSaveDialog(false);
        setShowShareDialog(true);
        toast.success('Bundle saved successfully!');
      } else {
        toast.error(data.detail || 'Failed to save bundle');
      }
    } catch (error) {
      console.error('Failed to save bundle:', error);
      toast.error('Failed to save bundle');
    } finally {
      setSaving(false);
    }
  };
  
  const copyShareLink = () => {
    const shareUrl = `${window.location.origin}/new-parent-starter-kit/${savedShareToken}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard!');
  };
  
  const shareViaEmail = () => {
    const shareUrl = `${window.location.origin}/new-parent-starter-kit/${savedShareToken}`;
    const subject = encodeURIComponent(`My New Parent Starter Kit - ${AGE_STAGES.find(s => s.id === selectedAge)?.label}`);
    const body = encodeURIComponent(`Check out my baby starter kit bundle!\n\n${shareUrl}\n\nTotal: ${currencySymbol}${totalPrice} (${itemCount} items)`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };
  
  // Auto-select recommended items
  const autoSelectRecommended = () => {
    const newSelected = {};
    Object.entries(ageItems).forEach(([category, items]) => {
      items.forEach(item => {
        if (item.recommended) {
          newSelected[item.id] = true;
        }
      });
    });
    setSelectedItems(newSelected);
    toast.success('Recommended items selected!');
  };
  
  // Toggle item selection
  const toggleItem = (itemId) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  // Calculate total
  const { totalPrice, itemCount, savings } = useMemo(() => {
    let total = 0;
    let count = 0;
    
    Object.entries(ageItems).forEach(([category, items]) => {
      items.forEach(item => {
        if (selectedItems[item.id]) {
          total += item.price;
          count++;
        }
      });
    });
    
    // Apply bundle discount
    const discountPercent = count >= 10 ? 0.15 : count >= 5 ? 0.10 : count >= 3 ? 0.05 : 0;
    const savingsAmount = Math.round(total * discountPercent);
    const finalPrice = total - savingsAmount;
    
    return { 
      totalPrice: finalPrice, 
      itemCount: count,
      savings: savingsAmount
    };
  }, [ageItems, selectedItems]);
  
  // Progress percentage
  const progress = (step / 3) * 100;
  
  // Loading state for shared bundles
  if (loadingShared) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50 py-8 px-4 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading shared bundle...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full mb-4">
            <Package className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">New Parent Starter Kit</h1>
          <p className="text-gray-600 mt-2">Build your perfect baby bundle with smart recommendations</p>
        </div>
        
        {/* Progress */}
        <div className="mb-8">
          <Progress value={progress} className="h-2" />
          <div className="flex justify-between mt-2 text-sm text-gray-500">
            <span className={step >= 1 ? 'text-indigo-600 font-medium' : ''}>1. Baby&apos;s Age</span>
            <span className={step >= 2 ? 'text-indigo-600 font-medium' : ''}>2. Select Items</span>
            <span className={step >= 3 ? 'text-indigo-600 font-medium' : ''}>3. Review &amp; Save</span>
          </div>
        </div>
        
        {/* Step 1: Select Age */}
        {step === 1 && (
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Baby className="w-5 h-5 text-pink-600" />
                Baby&apos;s Age Stage
              </CardTitle>
              <CardDescription>
                Select your baby&apos;s age to get personalized recommendations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Country Selection */}
              <div className="mb-6">
                <Label className="mb-2 block">Your Location</Label>
                <div className="flex gap-4">
                  <Button 
                    variant={country === 'UK' ? 'default' : 'outline'}
                    onClick={() => setCountry('UK')}
                    className="flex-1"
                  >
                    🇬🇧 United Kingdom
                  </Button>
                  <Button 
                    variant={country === 'LK' ? 'default' : 'outline'}
                    onClick={() => setCountry('LK')}
                    className="flex-1"
                  >
                    🇱🇰 Sri Lanka
                  </Button>
                </div>
              </div>
              
              {/* Age Selection */}
              <RadioGroup value={selectedAge} onValueChange={setSelectedAge}>
                <div className="space-y-3">
                  {AGE_STAGES.map(stage => {
                    const IconComponent = stage.icon;
                    return (
                      <label 
                        key={stage.id}
                        className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedAge === stage.id 
                            ? 'border-indigo-500 bg-indigo-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <RadioGroupItem value={stage.id} />
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{stage.label}</p>
                          <p className="text-sm text-gray-500">{stage.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </RadioGroup>
              
              <Button 
                className="w-full mt-4"
                onClick={() => setStep(2)}
                disabled={!selectedAge}
                data-testid="next-step-1"
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Step 2: Select Items */}
        {step === 2 && (
          <Card className="shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-indigo-600" />
                    Build Your Bundle
                  </CardTitle>
                  <CardDescription>
                    Select items for your {AGE_STAGES.find(s => s.id === selectedAge)?.label} bundle
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={autoSelectRecommended}>
                  <Sparkles className="w-4 h-4 mr-1" />
                  Auto-Select Recommended
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.entries(ageItems).map(([category, items]) => (
                <div key={category}>
                  <h3 className="font-semibold text-gray-900 mb-3 capitalize flex items-center gap-2">
                    {category === 'essentials' && <Package className="w-4 h-4" />}
                    {category === 'clothing' && <Shirt className="w-4 h-4" />}
                    {category === 'toys' && <Gamepad2 className="w-4 h-4" />}
                    {category === 'feeding' && <span>🍼</span>}
                    {category === 'travel' && <Car className="w-4 h-4" />}
                    {category === 'maternity' && <Heart className="w-4 h-4" />}
                    {category === 'nursery_prep' && <Package className="w-4 h-4" />}
                    {category.replace('_', ' ')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {items.map(item => (
                      <BundleItemCard 
                        key={item.id}
                        item={item}
                        selected={selectedItems[item.id] || false}
                        onToggle={() => toggleItem(item.id)}
                        currency={currency}
                      />
                    ))}
                  </div>
                </div>
              ))}
              
              {/* Running Total */}
              <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-indigo-700">Bundle Total ({itemCount} items)</p>
                    <p className="text-2xl font-bold text-indigo-900">{currencySymbol}{totalPrice}</p>
                    {savings > 0 && (
                      <p className="text-sm text-green-600">You save {currencySymbol}{savings} with bundle discount!</p>
                    )}
                  </div>
                  {itemCount >= 5 && (
                    <Badge className="bg-green-100 text-green-700">
                      {itemCount >= 10 ? '15% OFF' : itemCount >= 5 ? '10% OFF' : '5% OFF'}
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  className="flex-1"
                  onClick={() => setStep(3)}
                  disabled={itemCount === 0}
                  data-testid="next-step-2"
                >
                  Review Bundle
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Step 3: Review */}
        {step === 3 && (
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Your Starter Kit is Ready!
              </CardTitle>
              <CardDescription>
                Review your bundle and save or share it
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Summary */}
              <div className="p-4 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg text-white">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">
                    {AGE_STAGES.find(s => s.id === selectedAge)?.label} Bundle
                  </span>
                  <Badge className="bg-white/20 text-white">{itemCount} items</Badge>
                </div>
                <p className="text-3xl font-bold">{currencySymbol}{totalPrice}</p>
                {savings > 0 && (
                  <p className="text-sm text-indigo-200 mt-1">Includes {currencySymbol}{savings} bundle discount</p>
                )}
              </div>
              
              {/* Selected Items List */}
              <div className="space-y-2">
                <h3 className="font-semibold">Selected Items</h3>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {Object.entries(ageItems).map(([category, items]) => 
                    items.filter(item => selectedItems[item.id]).map(item => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <span>{item.icon}</span>
                          <span className="text-sm">{item.name}</span>
                        </div>
                        <span className="text-sm font-medium">
                          {currencySymbol}{item.price}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              {/* Tips */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                  <Star className="w-4 h-4" /> Smart Shopping Tips
                </h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Check our marketplace for second-hand items in great condition</li>
                  <li>• Safety items (car seats, gates) should always be bought new or inspected</li>
                  <li>• Start with essentials and add more as baby grows</li>
                </ul>
              </div>
              
              <div className="flex gap-3 flex-wrap">
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Edit Bundle
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShowSaveDialog(true)}
                  data-testid="open-save-dialog"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Bundle
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    toast.success('Browse our marketplace for these items!');
                    navigate(`/category/baby-kids-items`);
                  }}
                  data-testid="shop-now"
                >
                  Shop Items
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
                <Button 
                  className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600"
                  onClick={() => {
                    // Navigate to checkout with bundle data
                    const bundleData = {
                      ageStage: selectedAge,
                      items: Object.keys(selections).filter(k => selections[k]).map(k => {
                        const allItems = [
                          ...Object.values(currentBundle?.essentials || {}),
                          ...Object.values(currentBundle?.clothing || {}),
                          ...Object.values(currentBundle?.feeding || {}),
                          ...Object.values(currentBundle?.travel || {}),
                          ...Object.values(currentBundle?.toys || {}),
                        ].flat().find(i => i.id === k);
                        return allItems;
                      }).filter(Boolean),
                      totalPrice,
                      country
                    };
                    localStorage.setItem('starterKitBundle', JSON.stringify(bundleData));
                    navigate('/baby-kids/checkout');
                  }}
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Save Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Save className="w-5 h-5 text-purple-600" />
                Save Your Starter Kit
              </DialogTitle>
              <DialogDescription>
                Save your bundle to access it later or share with family
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input 
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  data-testid="save-email-input"
                />
              </div>
              <div>
                <Label htmlFor="name">Name (optional)</Label>
                <Input 
                  id="name"
                  placeholder="Your name"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                />
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>{AGE_STAGES.find(s => s.id === selectedAge)?.label}</strong> bundle with {itemCount} items
                </p>
                <p className="text-lg font-bold text-purple-700">{currencySymbol}{totalPrice}</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={saveBundle} 
                disabled={saving || !userEmail}
                data-testid="confirm-save"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Bundle
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Share Dialog */}
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-green-600" />
                Bundle Saved!
              </DialogTitle>
              <DialogDescription>
                Share your starter kit wishlist with family and friends
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-green-800 font-medium">Your bundle has been saved!</p>
              </div>
              
              <div>
                <Label className="text-sm text-gray-500">Share Link</Label>
                <div className="flex gap-2 mt-1">
                  <Input 
                    readOnly
                    value={`${window.location.origin}/new-parent-starter-kit/${savedShareToken || ''}`}
                    className="flex-1 bg-gray-50"
                  />
                  <Button variant="outline" size="icon" onClick={copyShareLink}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={copyShareLink}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={shareViaEmail}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button 
                className="w-full bg-gradient-to-r from-pink-500 to-purple-600"
                onClick={() => {
                  setShowShareDialog(false);
                  navigate(`/category/baby-kids-items`);
                }}
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Start Shopping
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
